import { Box, Button, Checkbox, Stack, Typography } from '@mui/material'
import { formatMiscHandledState } from '../../data/miscHandledStates'
import { MISC_PRIMARY } from '../../theme/onboardingTheme'

export type StateOption = { code: string; name: string }

type SelectedStatesPickerProps = {
  title: string
  states: StateOption[]
  selectedCodes: string[]
  onToggle: (code: string) => void
  onSelectAll: () => void
  onClearAll: () => void
  idPrefix: string
}

export function SelectedStatesPicker({
  title,
  states,
  selectedCodes,
  onToggle,
  onSelectAll,
  onClearAll,
  idPrefix,
}: SelectedStatesPickerProps) {
  const selectedSummary = states
    .filter((s) => selectedCodes.includes(s.code))
    .map((s) => formatMiscHandledState(s.code))
    .join(', ')

  return (
    <Box component="section">
      <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 600, color: '#0f172a' }}>
        {title}
      </Typography>

      {states.length === 0 ? (
        <Typography sx={{ mt: 1.5, fontSize: 14, color: '#475569' }}>
          No states selected on General Questions. Update your selected states there first.
        </Typography>
      ) : (
        <>
          <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap sx={{ mt: 1.5 }}>
            <Button
              type="button"
              onClick={onSelectAll}
              sx={{
                borderRadius: '8px',
                border: `1px solid ${MISC_PRIMARY}`,
                backgroundColor: '#ffffff',
                px: 2,
                py: 1,
                fontSize: 14,
                fontWeight: 600,
                color: MISC_PRIMARY,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#eff6ff' },
              }}
            >
              Select All States
            </Button>
            <Button
              type="button"
              onClick={onClearAll}
              sx={{
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                px: 2,
                py: 1,
                fontSize: 14,
                fontWeight: 600,
                color: '#475569',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#f8fafc' },
              }}
            >
              Clear All
            </Button>
          </Stack>

          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            }}
          >
            {states.map((st) => {
              const checked = selectedCodes.includes(st.code)
              const inputId = `${idPrefix}-${st.code}`
              return (
                <Box
                  key={st.code}
                  component="label"
                  htmlFor={inputId}
                  sx={{
                    display: 'flex',
                    cursor: 'pointer',
                    alignItems: 'center',
                    gap: 1,
                    borderRadius: '6px',
                    border: '1px solid transparent',
                    py: 0.5,
                    pr: 1,
                    '&:hover': { backgroundColor: '#f8fafc' },
                  }}
                >
                  <Checkbox
                    id={inputId}
                    checked={checked}
                    onChange={() => onToggle(st.code)}
                    size="small"
                    sx={{
                      p: 0,
                      color: '#cbd5e1',
                      '&.Mui-checked': { color: MISC_PRIMARY },
                    }}
                  />
                  <Typography component="span" sx={{ fontSize: 14, color: '#1e293b' }}>
                    {st.code} - {st.name}
                  </Typography>
                </Box>
              )
            })}
          </Box>

          <Box
            sx={{
              mt: 2,
              borderRadius: '8px',
              border: '1px solid #bfdbfe',
              backgroundColor: '#eff6ff',
              px: 2,
              py: 1.5,
              fontSize: 14,
              color: '#1e293b',
            }}
          >
            <Box component="span" sx={{ fontWeight: 600, color: '#0f172a' }}>
              Selected States:{' '}
            </Box>
            {selectedSummary || (
              <Box component="span" sx={{ color: '#64748b' }}>
                None selected yet.
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}
